import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Select,
  FormControl,
  InputLabel,
  Toolbar,
  Stack,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import FormatSizeIcon from '@mui/icons-material/FormatSize';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import FormatAlignJustify from '@mui/icons-material/FormatAlignJustify';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay } from 'date-fns';
import { tr } from 'date-fns/locale';
import { database } from '../../firebase';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { ref, get, set, update, push, remove } from 'firebase/database';
import firebaseDataService from '../../services/FirebaseDataService';

// Ajanda notu türü tanımı genişletildi
interface AgendaNote {
  id: string;
  date: string; // ISO formatında tarih
  content: string;
  formattedContent?: string; // HTML formatında içerik
  styles?: {
    fontFamily?: string;
    fontSize?: string;
    fontWeight?: string;
    fontStyle?: string;
    textDecoration?: string;
    color?: string;
    backgroundColor?: string;
    textAlign?: string;
  };
  createdAt: number;
  updatedAt: number;
}

interface AgendaProps {
  onBackToCalendar: () => void;
  initialDate?: Date;
}

// Kullanılabilir yazı tipleri
const AVAILABLE_FONT_FAMILIES = [
  { value: 'Arial, sans-serif', label: 'Arial' },
  { value: 'Calibri, sans-serif', label: 'Calibri' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Verdana, sans-serif', label: 'Verdana' },
  { value: 'Courier New, monospace', label: 'Courier New' },
  { value: 'Tahoma, sans-serif', label: 'Tahoma' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS' }
];

// Kullanılabilir yazı boyutları
const AVAILABLE_FONT_SIZES = [
  { value: 10, label: '10 pt' },
  { value: 11, label: '11 pt' },
  { value: 12, label: '12 pt' },
  { value: 14, label: '14 pt' },
  { value: 16, label: '16 pt' },
  { value: 18, label: '18 pt' },
  { value: 20, label: '20 pt' },
  { value: 22, label: '22 pt' },
  { value: 24, label: '24 pt' },
  { value: 26, label: '26 pt' },
  { value: 28, label: '28 pt' },
  { value: 36, label: '36 pt' },
  { value: 48, label: '48 pt' },
  { value: 72, label: '72 pt' }
];

// Kullanılabilir metin renkleri
const AVAILABLE_TEXT_COLORS = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF', 
  '#FFFF00', '#00FFFF', '#FF00FF', '#808080', '#FFA500'
];

// Kullanılabilir arkaplan renkleri
const AVAILABLE_BG_COLORS = [
  'transparent', '#FFFFFF', '#F5F5F5', '#FFFACD', '#E6F2FF', 
  '#FFEBEE', '#E8F5E9', '#FFF8E1', '#F3E5F5', '#E8EAF6'
];

const Agenda: React.FC<AgendaProps> = ({ onBackToCalendar, initialDate }) => {
  // Bugünün tarihini al
  const today = new Date();
  
  // Mini takvim için state
  const [miniCalendarMonth, setMiniCalendarMonth] = useState(initialDate || today);
  const [selectedDate, setSelectedDate] = useState(initialDate || today);
  
  // Ajanda notları için state'ler
  const [agendaNotes, setAgendaNotes] = useState<AgendaNote[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [noteContent, setNoteContent] = useState('');
  
  // Bildirim state'leri
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  
  // Format menüleri için state'ler
  const [textColorMenuAnchor, setTextColorMenuAnchor] = useState<null | HTMLElement>(null);
  const [bgColorMenuAnchor, setBgColorMenuAnchor] = useState<null | HTMLElement>(null);
  
  // Stil formatları için state'ler
  const [fontFamily, setFontFamily] = useState<string>('Arial, sans-serif');
  const [fontSize, setFontSize] = useState<string>('16px');
  const [fontStyles, setFontStyles] = useState<string[]>([]);
  const [textColor, setTextColor] = useState<string>('#000000');
  const [bgColor, setBgColor] = useState<string>('transparent');
  const [textAlign, setTextAlign] = useState<string>('left');
  
  // Düzenleme paneli için state
  const [showEditingPanel, setShowEditingPanel] = useState<boolean>(false);
  
  // Takvime geri dönmek için özel fonksiyon
  const handleBackToCalendar = () => {
    // Önce varsayılan geri dönüş fonksiyonunu çağır
    onBackToCalendar();
    
    // Ardından takvim butonlarının yeniden oluşturulması için bir zamanlayıcı ayarla
    setTimeout(() => {
      // Butonların yeniden oluşturulması için özel bir olay tetikle
      window.dispatchEvent(new Event('resize'));
      
      // DOM güncellendiğinde mutlaka butonların eklenmesini sağla
      setTimeout(() => {
        const rightHeaderEl = document.querySelector('.fc-toolbar-chunk:last-child');
        if (rightHeaderEl) {
          // Mevcut butonları temizle
          const existingCalendarBtn = document.querySelector('#add-calendar-event-btn');
          const existingBulkBtn = document.querySelector('#add-bulk-event-btn');
          
          if (existingCalendarBtn) {
            existingCalendarBtn.remove();
          }
          
          if (existingBulkBtn) {
            existingBulkBtn.remove();
          }
          
          // Özel etkinlik butonu oluştur
          const calendarEventButton = document.createElement('button');
          calendarEventButton.id = 'add-calendar-event-btn';
          calendarEventButton.className = 'fc-button fc-button-primary';
          calendarEventButton.innerHTML = '<span style="margin-right: 4px;">+</span> Özel Etkinlik Ekle';
          calendarEventButton.style.marginRight = '5px';
          
          // Özel etkinlik butonuna tıklama işlevi ekle
          calendarEventButton.addEventListener('click', () => {
            // Olayı tetiklemek için özel bir olay bırak
            const event = new CustomEvent('open-calendar-event-modal');
            document.dispatchEvent(event);
          });
          
          // Toplu etkinlik butonu oluştur
          const bulkEventButton = document.createElement('button');
          bulkEventButton.id = 'add-bulk-event-btn';
          bulkEventButton.className = 'fc-button fc-button-primary';
          bulkEventButton.innerHTML = '<span style="margin-right: 4px;">+</span> Toplu Etkinlik Ekle';
          bulkEventButton.style.marginRight = '5px';
          bulkEventButton.style.backgroundColor = '#FF9800'; // Turuncu arka plan
          bulkEventButton.style.borderColor = '#FF9800';
          
          // Toplu etkinlik butonuna tıklama işlevi ekle
          bulkEventButton.addEventListener('click', () => {
            // Olayı tetiklemek için özel bir olay bırak
            const event = new CustomEvent('open-bulk-event-modal');
            document.dispatchEvent(event);
          });
          
          // Butonları header'ın başına ekle
          const firstButton = rightHeaderEl.firstChild;
          if (firstButton) {
            rightHeaderEl.insertBefore(calendarEventButton, firstButton);
            rightHeaderEl.insertBefore(bulkEventButton, calendarEventButton);
          } else {
            rightHeaderEl.appendChild(bulkEventButton);
            rightHeaderEl.appendChild(calendarEventButton);
          }
        }
      }, 500);
    }, 100);
  };

  // Mini takvim için günleri localize etme fonksiyonu
  const getDayNames = () => {
    const days = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'];
    return days;
  };

  // Mini takvim için ayları localize etme fonksiyonu
  const getMonthNames = () => {
    const months = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];
    return months;
  };
  
  // Mini takvimde ay değiştirme
  const handlePrevMonth = () => {
    setMiniCalendarMonth(subMonths(miniCalendarMonth, 1));
  };

  const handleNextMonth = () => {
    setMiniCalendarMonth(addMonths(miniCalendarMonth, 1));
  };

  // Mini takvimde bugüne gitme
  const handleGoToToday = () => {
    setMiniCalendarMonth(today);
    setSelectedDate(today);
    loadAgendaNotes(today);
  };
  
  // Mini takvim için gün dizisi oluşturma
  const getDaysArray = () => {
    const monthStart = startOfMonth(miniCalendarMonth);
    const monthEnd = endOfMonth(miniCalendarMonth);
    const startDate = new Date(monthStart);
    const endDate = new Date(monthEnd);
    
    // Ayın ilk gününün haftanın hangi günü olduğunu bulma (0: Pazar, 1: Pazartesi, ..., 6: Cumartesi)
    const firstDayOfMonth = monthStart.getDay();
    
    // Pazartesi başlangıç için düzeltme (Pazartesi: 0, Salı: 1, ..., Pazar: 6)
    const firstDayOffset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
    
    // Başlangıç tarihini hesapla (ilk günden önceki Pazartesi'ye git)
    startDate.setDate(monthStart.getDate() - firstDayOffset);
    
    // Bitiş tarihini hesapla (son günden sonraki Pazar'a git)
    const lastDayOfMonth = monthEnd.getDay();
    const lastDayOffset = lastDayOfMonth === 0 ? 0 : 7 - lastDayOfMonth;
    endDate.setDate(monthEnd.getDate() + lastDayOffset);
    
    // Tarih aralığındaki tüm günleri al
    const daysArray = eachDayOfInterval({ start: startDate, end: endDate });
    
    return daysArray;
  };
  
  // Seçili tarihi değiştirme
  const handleDateChange = (date: Date) => {
    setSelectedDate(date);
    loadAgendaNotes(date);
  };
  
  // Ajanda notlarını yükleme
  const loadAgendaNotes = useCallback(async (date: Date) => {
    try {
      setLoadingNotes(true);
      
      // Giriş yapmış kullanıcıyı kontrol et
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) {
        setLoadingNotes(false);
        return;
      }
      
      // Kullanıcı verilerini al ve şirket ID'sini bul
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) {
        setLoadingNotes(false);
        return;
      }
      
      const companyId = userData.companyId || null;
      
      if (!companyId) {
        setLoadingNotes(false);
        return;
      }
      
      // Seçili tarih için ajanda notlarını al
      const formattedDate = date.toISOString().split('T')[0]; // YYYY-MM-DD formatı
      const notesRef = ref(database, `companies/${companyId}/agendaNotes/${formattedDate}`);
      const snapshot = await get(notesRef);
      
      if (!snapshot.exists()) {
        setAgendaNotes([]);
      } else {
        const notesData = snapshot.val();
        const notesList = Object.entries(notesData).map(([id, data]: [string, any]) => ({
          id,
          date: formattedDate,
          content: data.content || '',
          formattedContent: data.formattedContent || '',
          styles: data.styles || {},
          createdAt: data.createdAt || Date.now(),
          updatedAt: data.updatedAt || Date.now()
        }));
        
        // Oluşturma tarihine göre sırala
        notesList.sort((a, b) => b.createdAt - a.createdAt);
        
        setAgendaNotes(notesList);
      }
    } catch (error) {
      console.error('Ajanda notları yüklenirken hata:', error);
      setSnackbarMessage('Ajanda notları yüklenirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoadingNotes(false);
    }
  }, []);
  
  // Seçili tarihe göre notları yükleme
  useEffect(() => {
    if (selectedDate) {
      loadAgendaNotes(selectedDate);
    }
  }, [selectedDate, loadAgendaNotes]);
  
  // Yeni not ekle
  const handleAddNote = useCallback(async () => {
    try {
      // Giriş yapmış kullanıcıyı kontrol et
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) return;
      
      // Kullanıcı verilerini al ve şirket ID'sini bul
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) return;
      
      const companyId = userData.companyId || null;
      
      if (!companyId) return;
      
      // Yeni bir not oluştur
      const formattedDate = selectedDate.toISOString().split('T')[0]; // YYYY-MM-DD formatı
      const notesRef = ref(database, `companies/${companyId}/agendaNotes/${formattedDate}`);
      const newNoteRef = push(notesRef);
      
      const newNote = {
        content: '',
        formattedContent: '',
        styles: {},
        createdAt: Date.now(),
        updatedAt: Date.now(),
        createdBy: user.uid
      };
      
      // Veritabanına kaydet
      await set(newNoteRef, newNote);
      
      // UI'ı güncelle
      const noteId = newNoteRef.key;
      if (noteId) {
        setAgendaNotes([
          {
            id: noteId,
            date: formattedDate,
            content: '',
            formattedContent: '',
            styles: {},
            createdAt: newNote.createdAt,
            updatedAt: newNote.updatedAt
          },
          ...agendaNotes
        ]);
        
        // Yeni not düzenleme modunda başlasın
        setEditingNote(noteId);
        setNoteContent('');
      }
    } catch (error) {
      console.error('Not eklenirken hata:', error);
      setSnackbarMessage('Not eklenirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [selectedDate, agendaNotes]);

  // Not içeriğindeki biçimlendirmeyi HTML'e dönüştürür
  const formatNoteContent = useCallback((content: string, styles?: AgendaNote['styles']) => {
    if (!styles) return content;
    
    const fontWeight = fontStyles.includes('bold') ? 'bold' : 'normal';
    const fontStyle = fontStyles.includes('italic') ? 'italic' : 'normal';
    const textDecoration = fontStyles.includes('underline') ? 'underline' : 'none';
    
    return `<div style="
      font-family: ${styles.fontFamily || fontFamily};
      font-size: ${styles.fontSize || fontSize};
      font-weight: ${styles.fontWeight || fontWeight};
      font-style: ${styles.fontStyle || fontStyle};
      text-decoration: ${styles.textDecoration || textDecoration};
      color: ${styles.color || textColor};
      background-color: ${styles.backgroundColor || bgColor};
      text-align: ${styles.textAlign || textAlign};
    ">${content}</div>`;
  }, [fontFamily, fontSize, fontStyles, textColor, bgColor, textAlign]);
  
  // Font biçimini ayarla
  const handleFontStyleChange = (_: React.MouseEvent<HTMLElement>, newFormats: string[]) => {
    setFontStyles(newFormats);
  };
  
  // Metin hizalamasını değiştir
  const handleTextAlignChange = (_: React.MouseEvent<HTMLElement>, newAlignment: string | null) => {
    if (newAlignment !== null) {
      setTextAlign(newAlignment);
    }
  };
  
  // Metin rengi menüsünü aç/kapat
  const handleTextColorMenuClose = () => {
    setTextColorMenuAnchor(null);
  };
  
  const handleTextColorSelect = (color: string) => {
    setTextColor(color);
    handleTextColorMenuClose();
  };
  
  const handleTextColorMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTextColorMenuAnchor(event.currentTarget);
  };
  
  // Arkaplan rengi menüsünü aç/kapat
  const handleBgColorMenuClose = () => {
    setBgColorMenuAnchor(null);
  };
  
  const handleBgColorSelect = (color: string) => {
    setBgColor(color);
    handleBgColorMenuClose();
  };
  
  const handleBgColorMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setBgColorMenuAnchor(event.currentTarget);
  };
  
  // Not düzenlemeye başla
  const handleEditNote = useCallback((note: AgendaNote) => {
    setEditingNote(note.id);
    setNoteContent(note.content);
    
    // Eğer not için stil bilgileri varsa, onları ayarla
    if (note.styles) {
      note.styles.fontFamily && setFontFamily(note.styles.fontFamily);
      note.styles.fontSize && setFontSize(note.styles.fontSize);
      
      // Font stillerini ayarla
      const newStyles: string[] = [];
      if (note.styles.fontWeight === 'bold') newStyles.push('bold');
      if (note.styles.fontStyle === 'italic') newStyles.push('italic');
      if (note.styles.textDecoration === 'underline') newStyles.push('underline');
      setFontStyles(newStyles);
      
      note.styles.color && setTextColor(note.styles.color);
      note.styles.backgroundColor && setBgColor(note.styles.backgroundColor);
      note.styles.textAlign && setTextAlign(note.styles.textAlign);
    } else {
      // Stil bilgileri yoksa varsayılanları kullan
      setFontFamily('Arial, sans-serif');
      setFontSize('16px');
      setFontStyles([]);
      setTextColor('#000000');
      setBgColor('transparent');
      setTextAlign('left');
    }
  }, []);
  
  // Not kaydetme işlemi güncellendi
  const handleSaveNote = useCallback(async (noteId: string) => {
    try {
      // Giriş yapmış kullanıcıyı kontrol et
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) return;
      
      // Kullanıcı verilerini al ve şirket ID'sini bul
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) return;
      
      const companyId = userData.companyId || null;
      
      if (!companyId) return;
      
      // Stil bilgilerini hazırla
      const styles = {
        fontFamily,
        fontSize,
        fontWeight: fontStyles.includes('bold') ? 'bold' : 'normal',
        fontStyle: fontStyles.includes('italic') ? 'italic' : 'normal',
        textDecoration: fontStyles.includes('underline') ? 'underline' : 'none',
        color: textColor,
        backgroundColor: bgColor,
        textAlign
      };
      
      // Format edilmiş HTML içeriği oluştur
      const formattedContent = formatNoteContent(noteContent, styles);
      
      // Notu güncelle
      const formattedDate = selectedDate.toISOString().split('T')[0]; // YYYY-MM-DD formatı
      const noteRef = ref(database, `companies/${companyId}/agendaNotes/${formattedDate}/${noteId}`);
      
      const updatedNoteData = {
        content: noteContent,
        formattedContent,
        styles,
        updatedAt: Date.now()
      };
      
      // Veritabanını güncelle
      await update(noteRef, updatedNoteData);
      
      // UI'ı güncelle
      setAgendaNotes(agendaNotes.map(note => 
        note.id === noteId 
          ? { 
              ...note, 
              content: noteContent, 
              formattedContent,
              styles,
              updatedAt: updatedNoteData.updatedAt 
            }
          : note
      ));
      
      // Düzenleme modunu kapat
      setEditingNote(null);
      
      // Başarı mesajı göster
      setSnackbarMessage('Not başarıyla kaydedildi');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Not kaydedilirken hata:', error);
      setSnackbarMessage('Not kaydedilirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [selectedDate, noteContent, formatNoteContent, fontFamily, fontSize, fontStyles, textColor, bgColor, textAlign, agendaNotes]);

  // Not silme
  const handleDeleteNote = useCallback(async (noteId: string) => {
    try {
      // Giriş yapmış kullanıcıyı kontrol et
      const user = ownAuthSession.getCurrentUid() ? { uid: ownAuthSession.getCurrentUid() as string } : null;
      if (!user) return;
      
      // Kullanıcı verilerini al ve şirket ID'sini bul
      const userData = await firebaseDataService.getUserData(user.uid);
      if (!userData) return;
      
      const companyId = userData.companyId || null;
      
      if (!companyId) return;
      
      // Notu sil
      const formattedDate = selectedDate.toISOString().split('T')[0]; // YYYY-MM-DD formatı
      const noteRef = ref(database, `companies/${companyId}/agendaNotes/${formattedDate}/${noteId}`);
      
      // Veritabanından sil
      await remove(noteRef);
      
      // UI'dan sil
      setAgendaNotes(agendaNotes.filter(note => note.id !== noteId));
      
      // Düzenleme modu aktifse kapat
      if (editingNote === noteId) {
        setEditingNote(null);
      }
      
      // Başarı mesajı göster
      setSnackbarMessage('Not başarıyla silindi');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Not silinirken hata:', error);
      setSnackbarMessage('Not silinirken bir hata oluştu');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  }, [selectedDate, agendaNotes, editingNote]);
  
  // Not içeriğini güncelle
  const handleNoteContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteContent(e.target.value);
  }, []);
  
  // Seçili tarihin formatlanmış hali
  const formattedDate = format(selectedDate, 'dd MMMM yyyy', { locale: tr });
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Başlık */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            onClick={handleBackToCalendar}
            sx={{ mr: 1 }}
          >
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="h5">Ajanda - {formattedDate}</Typography>
        </Box>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleAddNote}
        >
          Yeni Not Ekle
        </Button>
      </Box>
      
      {/* Ana içerik */}
      <Box sx={{ flexGrow: 1, display: 'flex', gap: 3, height: 'calc(100% - 80px)' }}>
        {/* Sol taraf - Mini takvim */}
        <Box sx={{ width: 280 }}>
          <Paper elevation={3} sx={{ p: 2, height: 'fit-content' }}>
            {/* Mini takvim başlık */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <IconButton size="small" onClick={handlePrevMonth}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography variant="subtitle1" fontWeight="bold">
                {format(miniCalendarMonth, 'MMMM yyyy', { locale: tr })}
              </Typography>
              <IconButton size="small" onClick={handleNextMonth}>
                <ChevronRightIcon />
              </IconButton>
            </Box>
            
            {/* Bugüne git butonu */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Button 
                size="small" 
                onClick={handleGoToToday}
                startIcon={<TodayIcon />}
                variant="contained"
                sx={{ fontSize: '0.75rem' }}
              >
                Bugün
              </Button>
            </Box>
            
            {/* Gün isimleri */}
            <Grid container sx={{ mb: 1 }}>
              {getDayNames().map((day, index) => (
                <Grid item xs={12/7} key={index}>
                  <Typography 
                    variant="caption" 
                    align="center" 
                    sx={{ display: 'block', fontWeight: 'bold' }}
                  >
                    {day}
                  </Typography>
                </Grid>
              ))}
            </Grid>
            
            {/* Günler */}
            <Grid container>
              {getDaysArray().map((day, index) => (
                <Grid item xs={12/7} key={index}>
                  {day ? (
                    <Box 
                      sx={{ 
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '30px',
                        width: '30px',
                        margin: '0 auto',
                        cursor: 'pointer',
                        borderRadius: '50%',
                        bgcolor: isSameDay(day, selectedDate) ? 'primary.main' : 
                                 isSameDay(day, today) ? 'primary.light' : 
                                 'transparent',
                        color: isSameDay(day, selectedDate) ? 'white' : 
                               isSameDay(day, today) ? 'primary.contrastText' : 
                               !isSameMonth(day, miniCalendarMonth) ? 'text.disabled' : 
                               'text.primary',
                        '&:hover': {
                          bgcolor: isSameDay(day, selectedDate) ? 'primary.dark' : 'action.hover'
                        }
                      }}
                      onClick={() => handleDateChange(day)}
                    >
                      <Typography variant="caption">
                        {format(day, 'd')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ height: '30px' }} />
                  )}
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Box>
        
        {/* Sağ taraf - Notlar */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loadingNotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
              <CircularProgress />
            </Box>
          ) : agendaNotes.length === 0 ? (
            <Paper elevation={1} sx={{ p: 4, textAlign: 'center', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Bu tarih için henüz not eklenmemiş
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  "Yeni Not Ekle" butonuna tıklayarak ajandanıza not ekleyebilirsiniz.
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={handleAddNote}
                  sx={{ mt: 2 }}
                >
                  Not Ekle
                </Button>
              </Box>
            </Paper>
          ) : (
            <Box>
              {agendaNotes.map(note => (
                <Card key={note.id} sx={{ mb: 3, position: 'relative' }}>
                  <CardContent>
                    {editingNote === note.id ? (
                      <>
                        {/* Stil Araç Çubuğu */}
                        <Toolbar disableGutters sx={{ pr: 1, pl: 1, pt: 1, pb: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1 }}>
                            
                            {/* Font Family */}
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel id="font-family-select-label">Yazı Tipi</InputLabel>
                              <Select
                                labelId="font-family-select-label"
                                id="font-family-select"
                                value={fontFamily}
                                label="Yazı Tipi"
                                onChange={(e) => setFontFamily(e.target.value)}
                                MenuProps={{
                                  PaperProps: {
                                    style: {
                                      maxHeight: 300,
                                    },
                                  },
                                }}
                              >
                                {AVAILABLE_FONT_FAMILIES.map((font) => (
                                  <MenuItem key={font.value} value={font.value}>{font.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            
                            {/* Font Size */}
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel id="font-size-select-label">Boyut</InputLabel>
                              <Select
                                labelId="font-size-select-label"
                                id="font-size-select"
                                value={fontSize}
                                label="Boyut"
                                onChange={(e) => setFontSize(e.target.value)}
                              >
                                {AVAILABLE_FONT_SIZES.map((size) => (
                                  <MenuItem key={size.value} value={size.value}>{size.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>

                            {/* Font Styles */}
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                              <InputLabel id="font-style-select-label">Stil</InputLabel>
                              <Select
                                labelId="font-style-select-label"
                                id="font-style-select"
                                value={fontStyles.join(', ')}
                                label="Stil"
                                onChange={(e) => setFontStyles(e.target.value.split(', '))}
                              >
                                <MenuItem value="bold">Kalın</MenuItem>
                                <MenuItem value="italic">İtalik</MenuItem>
                                <MenuItem value="underline">Altı Çizili</MenuItem>
                              </Select>
                            </FormControl>

                            {/* Düzenleme Paneli Göster/Gizle */}
                            <FormControlLabel
                              control={
                                <Checkbox 
                                  checked={showEditingPanel} 
                                  onChange={(e) => setShowEditingPanel(e.target.checked)}
                                />
                              }
                              label="Düzenleme Paneli"
                            />
                          </Stack>
                        </Toolbar>
                        
                        {showEditingPanel && (
                          <Toolbar variant="dense" sx={{ bgcolor: '#f5f5f5', mb: 1, flexWrap: 'wrap', px: 1 }}>
                            <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
                              <Grid item xs={12}>
                                <Paper variant="outlined" sx={{ p: 1 }}>
                                  <ToggleButtonGroup
                                    value={fontStyles}
                                    onChange={handleFontStyleChange}
                                  >
                                    <Tooltip title="Kalın">
                                      <ToggleButton 
                                        value="bold" 
                                        selected={fontStyles.includes('bold')}
                                      >
                                        <FormatBoldIcon />
                                      </ToggleButton>
                                    </Tooltip>
                                    <Tooltip title="İtalik">
                                      <ToggleButton 
                                        value="italic" 
                                        selected={fontStyles.includes('italic')}
                                      >
                                        <FormatItalicIcon />
                                      </ToggleButton>
                                    </Tooltip>
                                    <Tooltip title="Altı Çizili">
                                      <ToggleButton 
                                        value="underline" 
                                        selected={fontStyles.includes('underline')}
                                      >
                                        <FormatUnderlinedIcon />
                                      </ToggleButton>
                                    </Tooltip>
                                  </ToggleButtonGroup>

                                  <ToggleButtonGroup
                                    exclusive
                                    value={textAlign}
                                    onChange={handleTextAlignChange}
                                    sx={{ ml: 1 }}
                                  >
                                    <Tooltip title="Sola Hizala">
                                      <ToggleButton value="left">
                                        <FormatAlignLeftIcon />
                                      </ToggleButton>
                                    </Tooltip>
                                    <Tooltip title="Ortala">
                                      <ToggleButton value="center">
                                        <FormatAlignCenterIcon />
                                      </ToggleButton>
                                    </Tooltip>
                                    <Tooltip title="Sağa Hizala">
                                      <ToggleButton value="right">
                                        <FormatAlignRightIcon />
                                      </ToggleButton>
                                    </Tooltip>
                                    <Tooltip title="İki Yana Yasla">
                                      <ToggleButton value="justify">
                                        <FormatAlignJustify />
                                      </ToggleButton>
                                    </Tooltip>
                                  </ToggleButtonGroup>

                                  <FormControl size="small" sx={{ m: 0.5, minWidth: 120 }}>
                                    <InputLabel id="font-family-select-label">Yazı Tipi</InputLabel>
                                    <Select
                                      labelId="font-family-select-label"
                                      id="font-family-select"
                                      value={fontFamily}
                                      label="Yazı Tipi"
                                      onChange={(e) => setFontFamily(e.target.value)}
                                    >
                                      {AVAILABLE_FONT_FAMILIES.map((font) => (
                                        <MenuItem key={font.value} value={font.value}>{font.label}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>

                                  <FormControl size="small" sx={{ m: 0.5, minWidth: 80 }}>
                                    <InputLabel id="font-size-select-label">Boyut</InputLabel>
                                    <Select
                                      labelId="font-size-select-label"
                                      id="font-size-select"
                                      value={fontSize}
                                      label="Boyut"
                                      onChange={(e) => setFontSize(e.target.value)}
                                    >
                                      {AVAILABLE_FONT_SIZES.map((size) => (
                                        <MenuItem key={size.value} value={size.value}>{size.label}</MenuItem>
                                      ))}
                                    </Select>
                                  </FormControl>

                                  {/* Metin Rengi Düğmesi */}
                                  <Tooltip title="Metin Rengi">
                                    <Button
                                      size="small"
                                      sx={{ 
                                        m: 0.5, 
                                        minWidth: 'unset', 
                                        border: '1px solid rgba(0,0,0,0.12)',
                                        backgroundColor: textColor,
                                        color: textColor === '#000000' ? 'white' : 'black'
                                      }}
                                      onClick={handleTextColorMenuOpen}
                                    >
                                      <FormatColorTextIcon />
                                    </Button>
                                  </Tooltip>
                                  
                                  {/* Arkaplan Rengi Düğmesi */}
                                  <Tooltip title="Arkaplan Rengi">
                                    <Button
                                      size="small"
                                      sx={{ 
                                        m: 0.5, 
                                        minWidth: 'unset', 
                                        border: '1px solid rgba(0,0,0,0.12)',
                                        backgroundColor: bgColor === 'transparent' ? 'white' : bgColor,
                                        color: 'black' 
                                      }}
                                      onClick={handleBgColorMenuOpen}
                                    >
                                      <FormatColorFillIcon />
                                    </Button>
                                  </Tooltip>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Toolbar>
                        )}
                        
                        {/* Metin Rengi Menüsü */}
                        <Menu
                          anchorEl={textColorMenuAnchor}
                          open={Boolean(textColorMenuAnchor)}
                          onClose={handleTextColorMenuClose}
                        >
                          <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', width: 220 }}>
                            {AVAILABLE_TEXT_COLORS.map((color) => (
                              <IconButton
                                key={color}
                                onClick={() => handleTextColorSelect(color)}
                                sx={{
                                  m: 0.5,
                                  width: 30,
                                  height: 30,
                                  backgroundColor: color,
                                  border: '1px solid rgba(0,0,0,0.12)',
                                  '&:hover': { backgroundColor: color, opacity: 0.8 }
                                }}
                              />
                            ))}
                          </Box>
                        </Menu>

                        {/* Arkaplan Rengi Menüsü */}
                        <Menu
                          anchorEl={bgColorMenuAnchor}
                          open={Boolean(bgColorMenuAnchor)}
                          onClose={handleBgColorMenuClose}
                        >
                          <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', width: 220 }}>
                            {AVAILABLE_BG_COLORS.map((color) => (
                              <IconButton
                                key={color}
                                onClick={() => handleBgColorSelect(color)}
                                sx={{
                                  m: 0.5,
                                  width: 30,
                                  height: 30,
                                  backgroundColor: color,
                                  border: '1px solid rgba(0,0,0,0.12)',
                                  '&:hover': { backgroundColor: color, opacity: 0.8 }
                                }}
                              />
                            ))}
                          </Box>
                        </Menu>
                          
                        {/* Formatlanmış metin stilini önizleme */}
                        <Box 
                          sx={{ 
                            border: '1px solid rgba(0, 0, 0, 0.23)',
                            borderRadius: 1,
                            p: 1,
                            mb: 2,
                            fontFamily,
                            fontSize,
                            fontWeight: fontStyles.includes('bold') ? 'bold' : 'normal',
                            fontStyle: fontStyles.includes('italic') ? 'italic' : 'normal',
                            textDecoration: fontStyles.includes('underline') ? 'underline' : 'none',
                            color: textColor,
                            bgcolor: bgColor,
                            textAlign,
                            minHeight: '30px'
                          }}
                        >
                          Metin önizleme
                        </Box>
                        
                        {/* Not Metin Alanı */}
                        <TextField
                          multiline
                          fullWidth
                          variant="outlined"
                          value={noteContent}
                          onChange={handleNoteContentChange}
                          sx={{
                            mb: 2,
                            '& .MuiInputBase-input': {
                              fontFamily,
                              fontSize,
                              fontWeight: fontStyles.includes('bold') ? 'bold' : 'normal',
                              fontStyle: fontStyles.includes('italic') ? 'italic' : 'normal',
                              textDecoration: fontStyles.includes('underline') ? 'underline' : 'none',
                              color: textColor,
                              backgroundColor: bgColor,
                              textAlign: textAlign as any
                            }
                          }}
                          minRows={5}
                        />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                          <Button 
                            onClick={() => setEditingNote(null)} 
                            sx={{ mr: 1 }}
                          >
                            İptal
                          </Button>
                          <Button 
                            variant="contained" 
                            color="primary"
                            startIcon={<SaveIcon />}
                            onClick={() => handleSaveNote(note.id)}
                          >
                            Kaydet
                          </Button>
                        </Box>
                      </>
                    ) : (
                      <>
                        {/* Not içeriğini göster (HTML formatında) */}
                        {note.formattedContent ? (
                          <Box 
                            dangerouslySetInnerHTML={{ __html: note.formattedContent }} 
                            sx={{ 
                              whiteSpace: 'pre-wrap',
                              minHeight: '80px'
                            }}
                          />
                        ) : (
                          <Typography sx={{ 
                            whiteSpace: 'pre-wrap',
                            minHeight: '80px',
                            ...(note.styles && {
                              fontFamily: note.styles.fontFamily,
                              fontSize: note.styles.fontSize,
                              fontWeight: note.styles.fontWeight,
                              fontStyle: note.styles.fontStyle,
                              textDecoration: note.styles.textDecoration,
                              color: note.styles.color,
                              backgroundColor: note.styles.backgroundColor,
                              textAlign: note.styles.textAlign as any
                            })
                          }}>
                            {note.content || <i>Boş not</i>}
                          </Typography>
                        )}
                        
                        <Divider sx={{ my: 2 }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            Son güncelleme: {format(new Date(note.updatedAt), 'dd MMM yyyy HH:mm', { locale: tr })}
                          </Typography>
                          
                          <Box>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditNote(note)}
                              sx={{ mr: 1 }}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteNote(note.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      
      {/* Bildirim Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Agenda; 